import React, { useState, useEffect } from 'react';
import { invoicesApi } from '../../services/crmApi';
import {
  Receipt, Plus, Send, Trash2, FileText, ExternalLink, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Check, X,
} from 'lucide-react';

interface Props {
  applicationId: number;
  buyerName: string;
  buyerEmail?: string;
  buyerPhone?: string;
  buyerNip?: string;
  serviceType?: string;
}

interface InvoicePosition {
  name: string;
  quantity: number;
  tax: number;
  total_price_gross: number;
  unit: string;
}

const SERVICE_PRODUCTS: Record<string, { name: string; grossPrice: number; tax: number }[]> = {
  pup_grant: [
    { name: 'Usługa doradcza — wniosek o dotację PUP', grossPrice: 2460, tax: 23 },
    { name: 'Kompletacja dokumentacji do wniosku PUP', grossPrice: 1230, tax: 23 },
    { name: 'Napisanie biznesplanu PUP', grossPrice: 1845, tax: 23 },
  ],
  wsparcie_na_starcie: [
    { name: 'Obsługa wniosku o pożyczkę Wsparcie na Starcie', grossPrice: 8200, tax: 23 },
    { name: 'Biznesplan do pożyczki WnS', grossPrice: 3690, tax: 23 },
    { name: 'Kosztorys inwestycji', grossPrice: 1230, tax: 23 },
  ],
  kfs: [
    { name: 'Doradztwo KFS — analiza potrzeb szkoleniowych', grossPrice: 1230, tax: 23 },
    { name: 'Obsługa wniosku KFS', grossPrice: 2460, tax: 23 },
  ],
  czyste_powietrze: [
    { name: 'Obsługa wniosku Czyste Powietrze', grossPrice: 3690, tax: 23 },
    { name: 'Audyt energetyczny budynku', grossPrice: 1845, tax: 23 },
    { name: 'Nadzór nad realizacją prac', grossPrice: 4920, tax: 23 },
    { name: 'Rozliczenie końcowe dotacji', grossPrice: 2460, tax: 23 },
  ],
  uslugi_ksiegowe: [
    { name: 'Usługi księgowe — pakiet Start (JDG)', grossPrice: 369, tax: 23 },
    { name: 'Usługi księgowe — pakiet Standard (sp. z o.o.)', grossPrice: 799, tax: 23 },
    { name: 'Usługi księgowe — pakiet Premium (pełna księgowość)', grossPrice: 1599, tax: 23 },
    { name: 'Założenie działalności gospodarczej', grossPrice: 615, tax: 23 },
    { name: 'Zmiana formy prawnej / przekształcenie', grossPrice: 2460, tax: 23 },
  ],
  wycieczki: [
    { name: 'Wycieczka z przewodnikiem — Nowa Huta', grossPrice: 600, tax: 23 },
    { name: 'Wycieczka grupowa — Kombinat', grossPrice: 1200, tax: 23 },
    { name: 'Wycieczka tematyczna — Szlakiem PRL', grossPrice: 900, tax: 23 },
    { name: 'Usługa przewodnicka — wycieczka indywidualna', grossPrice: 350, tax: 23 },
    { name: 'Wynajem busa / transport grupowy', grossPrice: 800, tax: 23 },
  ],
  turnusy: [
    { name: 'Turnus rehabilitacyjny — pobyt (14 dni)', grossPrice: 3500, tax: 'zw' as any },
    { name: 'Turnus rehabilitacyjny — pobyt (21 dni)', grossPrice: 4800, tax: 'zw' as any },
    { name: 'Dopłata do pokoju 1-osobowego', grossPrice: 700, tax: 'zw' as any },
    { name: 'Pakiet zabiegów dodatkowych', grossPrice: 500, tax: 'zw' as any },
    { name: 'Transport na turnus (Kraków — ośrodek)', grossPrice: 200, tax: 23 },
  ],
  hutnik_biznes: [
    { name: 'Pakiet Sponsor — Hutnik Business Club', grossPrice: 10000, tax: 23 },
    { name: 'Pakiet Partner — Hutnik Business Club', grossPrice: 5000, tax: 23 },
    { name: 'Pakiet Premium — Hutnik Business Club', grossPrice: 25000, tax: 23 },
    { name: 'Wynajem loży VIP — mecz Hutnik', grossPrice: 2000, tax: 23 },
    { name: 'Branding na koszulkach — sezon', grossPrice: 15000, tax: 23 },
  ],
  granty_fpn: [
    { name: 'Obsługa wniosku grantowego', grossPrice: 4920, tax: 23 },
    { name: 'Napisanie wniosku o dofinansowanie', grossPrice: 8610, tax: 23 },
    { name: 'Rozliczenie grantu / sprawozdanie', grossPrice: 3690, tax: 23 },
    { name: 'Konsultacja — analiza kwalifikowalności', grossPrice: 1230, tax: 23 },
  ],
  dotacje_rozwojowe: [
    { name: 'Analiza kwalifikowalności MŚP', grossPrice: 2460, tax: 23 },
    { name: 'Przygotowanie wniosku o dotację rozwojową', grossPrice: 6150, tax: 23 },
    { name: 'Rozliczenie dotacji rozwojowej', grossPrice: 4920, tax: 23 },
    { name: 'Biznesplan do dotacji', grossPrice: 3690, tax: 23 },
  ],
  marketing_beauty: [
    { name: 'Pakiet branding — identyfikacja wizualna', grossPrice: 3690, tax: 23 },
    { name: 'Obsługa social media — pakiet miesięczny', grossPrice: 2460, tax: 23 },
    { name: 'Strona internetowa — landing page', grossPrice: 4920, tax: 23 },
    { name: 'Sesja zdjęciowa — produkt/usługa', grossPrice: 1845, tax: 23 },
  ],
  gastro_consulting: [
    { name: 'Audyt gastronomiczny — lokal', grossPrice: 3690, tax: 23 },
    { name: 'Optymalizacja menu i food cost', grossPrice: 2460, tax: 23 },
    { name: 'Szkolenie personelu kuchni', grossPrice: 1845, tax: 23 },
    { name: 'Konsultacja — otwarcie lokalu gastro', grossPrice: 4920, tax: 23 },
  ],
  default: [
    { name: 'Usługa doradcza', grossPrice: 1230, tax: 23 },
    { name: 'Konsultacja', grossPrice: 615, tax: 23 },
  ],
};

const FAKTUROWNIA_BASE = 'https://fundacjanh.fakturownia.pl';

export const InvoicesSection: React.FC<Props> = ({
  applicationId, buyerName, buyerEmail, buyerPhone, buyerNip, serviceType,
}) => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New invoice form
  const [kind, setKind] = useState<'vat' | 'proforma'>('vat');
  const [positions, setPositions] = useState<InvoicePosition[]>([
    { name: '', quantity: 1, tax: 23, total_price_gross: 0, unit: 'szt.' },
  ]);
  const [description, setDescription] = useState('');

  useEffect(() => {
    loadInvoices();
  }, [applicationId]);

  const loadInvoices = async () => {
    try {
      const status = await invoicesApi.getStatus();
      setConfigured(status.configured);
      if (!status.configured) { setLoading(false); return; }

      const data = await invoicesApi.getByApplication(applicationId);
      setInvoices(data.invoices || []);
    } catch (err: any) {
      console.error('Load invoices error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const validPositions = positions.filter(p => p.name.trim() && p.total_price_gross > 0);
    if (validPositions.length === 0) {
      setError('Dodaj co najmniej jedną pozycję z nazwą i kwotą');
      return;
    }

    setCreating(true);
    setError('');
    try {
      await invoicesApi.createFromApplication(applicationId, {
        positions: validPositions,
        kind,
        description: description || undefined,
      });
      setSuccess('Faktura utwórzona');
      setShowForm(false);
      setPositions([{ name: '', quantity: 1, tax: 23, total_price_gross: 0, unit: 'szt.' }]);
      setDescription('');
      await loadInvoices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async (invoiceId: number) => {
    setSending(invoiceId);
    try {
      await invoicesApi.sendByEmail(invoiceId);
      setSuccess('Faktura wysłana emailem');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd wysyłania');
    } finally {
      setSending(null);
    }
  };

  const addPosition = () => {
    setPositions(prev => [...prev, { name: '', quantity: 1, tax: 23, total_price_gross: 0, unit: 'szt.' }]);
  };

  const removePosition = (index: number) => {
    setPositions(prev => prev.filter((_, i) => i !== index));
  };

  const updatePosition = (index: number, field: keyof InvoicePosition, value: any) => {
    setPositions(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const applyTemplate = (template: { name: string; grossPrice: number; tax: number }) => {
    setPositions([{
      name: template.name,
      quantity: 1,
      tax: template.tax,
      total_price_gross: template.grossPrice,
      unit: 'szt.',
    }]);
  };

  const templates = SERVICE_PRODUCTS[serviceType || ''] || SERVICE_PRODUCTS.default;
  const totalGross = positions.reduce((s, p) => s + (p.total_price_gross * p.quantity), 0);

  if (!configured) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-2 flex items-center">
          <Receipt size={18} className="mr-2 text-gray-400" />
          Faktury
        </h3>
        <p className="text-sm text-gray-500">Fakturownia nie jest skonfigurowana. Dodaj FAKTUROWNIA_TOKEN w ustawieniach serwera.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="font-bold flex items-center">
          <Receipt size={18} className="mr-2 text-green-600" />
          Faktury
          {invoices.length > 0 && (
            <span className="ml-2 bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">
              {invoices.length}
            </span>
          )}
        </h3>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Status messages */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
              <AlertCircle size={14} />
              {error}
              <button type="button" onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-2 rounded">
              <Check size={14} />
              {success}
            </div>
          )}

          {/* Existing invoices */}
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Loader2 size={14} className="animate-spin" />
              Ładowanie faktur...
            </div>
          ) : invoices.length > 0 ? (
            <div className="space-y-2">
              {invoices.map((inv: any) => (
                <div key={inv.id} className="border rounded-lg p-3 text-sm hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{inv.number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      inv.status === 'paid' ? 'bg-green-100 text-green-700'
                        : inv.status === 'sent' ? 'bg-blue-100 text-blue-700'
                        : inv.status === 'rejected' ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {inv.status === 'paid' ? 'Opłacona' : inv.status === 'sent' ? 'Wysłana' : inv.status === 'rejected' ? 'Odrzucona' : inv.status || 'Nowa'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>{inv.issue_date}</span>
                    <span className="font-bold text-gray-900">{Number(inv.price_gross).toLocaleString('pl-PL')} zł</span>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <a
                      href={`${FAKTUROWNIA_BASE}/invoices/${inv.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 bg-blue-50 rounded"
                    >
                      <ExternalLink size={12} /> Otwórz
                    </a>
                    <button
                      type="button"
                      onClick={() => handleSend(inv.id)}
                      disabled={sending === inv.id}
                      className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 px-2 py-1 bg-green-50 rounded disabled:opacity-50"
                    >
                      {sending === inv.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Email
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Brak faktur dla tego wniosku.</p>
          )}

          {/* Create invoice button / form */}
          {!showForm ? (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
            >
              <Plus size={16} /> Wystaw fakturę
            </button>
          ) : (
            <div className="border-2 border-green-200 rounded-lg p-4 space-y-3 bg-green-50/50">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Nowa faktura</h4>
                <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>

              {/* Buyer info (read-only from CRM) */}
              <div className="text-xs text-gray-600 bg-white rounded p-2">
                <p className="font-medium">{buyerName}</p>
                {buyerNip && <p>NIP: {buyerNip}</p>}
                {buyerEmail && <p>{buyerEmail}</p>}
              </div>

              {/* Invoice type */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setKind('vat')}
                  className={`flex-1 py-1.5 text-xs font-medium rounded border transition-colors ${kind === 'vat' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}
                >
                  Faktura VAT
                </button>
                <button
                  type="button"
                  onClick={() => setKind('proforma')}
                  className={`flex-1 py-1.5 text-xs font-medium rounded border transition-colors ${kind === 'proforma' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}
                >
                  Proforma
                </button>
              </div>

              {/* Quick templates */}
              <div>
                <p className="text-xs text-gray-500 mb-1">Szablony:</p>
                <div className="flex flex-wrap gap-1">
                  {templates.map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="text-xs px-2 py-1 bg-white border rounded hover:bg-green-50 hover:border-green-300 transition-colors truncate max-w-full"
                      title={`${t.name} — ${t.grossPrice} zł brutto`}
                    >
                      {t.name.length > 35 ? t.name.slice(0, 35) + '…' : t.name} ({t.grossPrice} zł)
                    </button>
                  ))}
                </div>
              </div>

              {/* Positions */}
              <div className="space-y-2">
                {positions.map((pos, i) => (
                  <div key={i} className="bg-white rounded border p-2 space-y-1.5">
                    <input
                      type="text"
                      value={pos.name}
                      onChange={e => updatePosition(i, 'name', e.target.value)}
                      placeholder="Nazwa usługi / produktu"
                      className="w-full border rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-green-500"
                    />
                    <div className="grid grid-cols-4 gap-1.5">
                      <div>
                        <label className="block text-[10px] text-gray-500">Ilość</label>
                        <input
                          type="number"
                          min="1"
                          value={pos.quantity}
                          onChange={e => updatePosition(i, 'quantity', parseInt(e.target.value, 10) || 1)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500">VAT %</label>
                        <select
                          value={pos.tax}
                          onChange={e => updatePosition(i, 'tax', parseInt(e.target.value, 10))}
                          className="w-full border rounded px-1 py-1 text-sm"
                          aria-label="Stawka VAT"
                        >
                          <option value={23}>23%</option>
                          <option value={8}>8%</option>
                          <option value={0}>0%</option>
                          <option value={-1}>zw.</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500">Brutto (zł)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pos.total_price_gross || ''}
                          onChange={e => updatePosition(i, 'total_price_gross', parseFloat(e.target.value) || 0)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        {positions.length > 1 && (
                          <button type="button" onClick={() => removePosition(i)} className="p-1 text-red-400 hover:text-red-600">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addPosition} className="w-full py-1 text-xs text-green-600 hover:text-green-800 border border-dashed border-green-300 rounded hover:border-green-500 transition-colors">
                  + Dodaj pozycję
                </button>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center bg-white rounded p-2 border">
                <span className="text-sm text-gray-600">Suma brutto:</span>
                <span className="text-lg font-bold text-green-700">{totalGross.toLocaleString('pl-PL')} zł</span>
              </div>

              {/* Description */}
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Opis (opcjonalnie)"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />

              {/* Submit */}
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm transition-colors"
              >
                {creating ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {creating ? 'Tworzenie...' : `Wystaw ${kind === 'proforma' ? 'proformę' : 'fakturę VAT'}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
